import React, { useEffect, useRef, useState } from 'react';
import { EditIcon, StadiumIcon } from '../UI/Icons';
import { Vendor } from '../../utils/constants';
import styles from './VendorCard.module.css';

interface VendorCardProps {
  vendor: Vendor;
  onManage: () => void;
  onEditSections: () => void;
  onEditDetails?: () => void;
  onToggleStatus?: (vendor: Vendor, status: 'active' | 'inactive') => void;
  onUpdateWait?: (vendor: Vendor, waitMinutes: number | null) => void;
  onDelete?: (vendor: Vendor) => void;
}

function formatSections(sections?: string[]) {
  if (!sections || sections.length === 0) return 'No sections';
  if (sections.length <= 3) return sections.join(', ');
  return `${sections.slice(0, 3).join(', ')} +${sections.length - 3}`;
}

function formatWait(minutes: number | null | undefined) {
  if (minutes == null) return '—';
  return `${minutes} min`;
}

const VendorCard: React.FC<VendorCardProps> = ({
  vendor,
  onManage,
  onEditSections,
  onEditDetails,
  onToggleStatus,
  onUpdateWait,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [waitOpen, setWaitOpen] = useState(false);
  const [waitInput, setWaitInput] = useState(
    vendor.currentWaitMinutes != null ? String(vendor.currentWaitMinutes) : ''
  );
  const menuRef = useRef<HTMLDivElement>(null);

  const menuItemCount = vendor.menuItems?.length || 0;
  const isActive = (vendor.status || 'active') === 'active';

  useEffect(() => {
    setWaitInput(vendor.currentWaitMinutes != null ? String(vendor.currentWaitMinutes) : '');
  }, [vendor.currentWaitMinutes]);

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setWaitOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleToggleStatus = () => {
    const next = isActive ? 'inactive' : 'active';
    const label = isActive ? 'pause' : 'activate';
    if (window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} "${vendor.name}"?`)) {
      onToggleStatus?.(vendor, next);
    }
    setMenuOpen(false);
  };

  const handleSaveWait = () => {
    const parsed = waitInput.trim() === '' ? null : parseInt(waitInput, 10);
    onUpdateWait?.(vendor, Number.isFinite(parsed) ? parsed : null);
    setWaitOpen(false);
    setMenuOpen(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete "${vendor.name}" and all menu items? This cannot be undone.`)) {
      onDelete?.(vendor);
    }
    setMenuOpen(false);
  };

  return (
    <div className={`${styles.card} ${!isActive ? styles.cardInactive : ''}`}>
      <div className={styles.content}>
        <div className={styles.avatar}>
          {vendor.image ? (
            <img src={vendor.image} alt="" className={styles.avatarImage} />
          ) : (
            vendor.name.charAt(0).toUpperCase()
          )}
        </div>

        <div className={styles.info}>
          <div className={styles.nameRow}>
            <h3 className={styles.name}>{vendor.name}</h3>
            <button
              type="button"
              className={`${styles.statusChip} ${isActive ? styles.statusChipActive : styles.statusChipPaused}`}
              onClick={handleToggleStatus}
              title={isActive ? 'Click to pause vendor' : 'Click to activate vendor'}
            >
              {isActive ? 'Active' : 'Paused'}
            </button>
          </div>

          {vendor.cuisine_type && (
            <p className={styles.cuisine}>{vendor.cuisine_type.replace(/[-_]/g, ' ')}</p>
          )}

          <div className={styles.details}>
            <div className={styles.detail}>
              <StadiumIcon size={16} className={styles.detailIcon} />
              <div className={styles.detailContent}>
                <p className={styles.detailLabel}>Sections</p>
                <p className={styles.detailValue}>{formatSections(vendor.sections)}</p>
              </div>
            </div>

            <div className={styles.detail}>
              <div className={styles.detailEmoji}>📋</div>
              <div className={styles.detailContent}>
                <p className={styles.detailLabel}>Menu items</p>
                <p className={styles.detailValue}>
                  {menuItemCount} {menuItemCount === 1 ? 'item' : 'items'}
                </p>
              </div>
            </div>

            <div className={styles.detail}>
              <div className={styles.detailEmoji}>⏱</div>
              <div className={styles.detailContent}>
                <p className={styles.detailLabel}>Wait</p>
                <p className={styles.detailValue}>{formatWait(vendor.currentWaitMinutes)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            onClick={onManage}
            className={`${styles.action} ${styles.actionPrimary}`}
            aria-label={`Manage menu for ${vendor.name}`}
          >
            Manage menu
          </button>

          <button
            type="button"
            onClick={onEditSections}
            className={`${styles.action} ${styles.actionSecondary}`}
            aria-label={`Edit sections for ${vendor.name}`}
          >
            <EditIcon size={16} />
            Edit sections
          </button>

          <div className={styles.menuWrap} ref={menuRef}>
            <button
              type="button"
              className={styles.kebab}
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={`More actions for ${vendor.name}`}
              aria-expanded={menuOpen}
            >
              ⋮
            </button>

            {menuOpen && (
              <div className={styles.dropdown}>
                <button type="button" className={styles.dropdownItem} onClick={handleToggleStatus}>
                  {isActive ? 'Pause vendor' : 'Activate vendor'}
                </button>
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => setWaitOpen((open) => !open)}
                >
                  Set wait time…
                </button>
                {waitOpen && (
                  <div className={styles.waitPopover}>
                    <label className={styles.waitLabel} htmlFor={`wait-${vendor.id}`}>
                      Minutes
                    </label>
                    <div className={styles.waitRow}>
                      <input
                        id={`wait-${vendor.id}`}
                        type="number"
                        min="0"
                        className={styles.waitInput}
                        value={waitInput}
                        onChange={(e) => setWaitInput(e.target.value)}
                        placeholder="Clear"
                      />
                      <button type="button" className={styles.waitSave} onClick={handleSaveWait}>
                        Save
                      </button>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => {
                    onEditDetails?.();
                    setMenuOpen(false);
                  }}
                >
                  Edit vendor details
                </button>
                {onDelete && (
                  <button
                    type="button"
                    className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                    onClick={handleDelete}
                  >
                    Delete vendor
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorCard;
